/**
 * trafficSource — classify a referrer URL / click IDs into a marketing
 * source + medium, mirroring public.classify_traffic_source() in SQL
 * (latest: migration 20260927100000_traffic_source_ai_and_ios_clicks.sql).
 *
 * Keep the two in sync: the client copy labels first-touch data at capture
 * time; the SQL copy classifies historical rows and analytics rollups.
 */

export interface ClassifiedSource {
  source: string;
  medium: string;
}

/** Hostnames that belong to us — a referrer from these is internal, not a source. */
const INTERNAL_HOSTS = [
  "thailand-elite.com",
  "thailandelite.fr",
  "portal.thailand-elite.com",
  "localhost",
  ".pages.dev",
];

const SEARCH_ENGINES: Record<string, string> = {
  google: "google",
  bing: "bing",
  yahoo: "yahoo",
  duckduckgo: "duckduckgo",
  baidu: "baidu",
  yandex: "yandex",
  naver: "naver",
  ecosia: "ecosia",
  qwant: "qwant",
};

const SOCIAL_NETWORKS: Record<string, string> = {
  facebook: "facebook",
  "fb.com": "facebook",
  instagram: "instagram",
  "twitter.com": "x",
  "x.com": "x",
  "t.co": "x",
  linkedin: "linkedin",
  "lnkd.in": "linkedin",
  youtube: "youtube",
  "youtu.be": "youtube",
  tiktok: "tiktok",
  reddit: "reddit",
  pinterest: "pinterest",
  quora: "quora",
  telegram: "telegram",
  "t.me": "telegram",
  whatsapp: "whatsapp",
  "wa.me": "whatsapp",
  "line.me": "line",
  vk_com: "vk",
  weibo: "weibo",
};

const AI_ASSISTANTS: Record<string, string> = {
  "chatgpt.com": "chatgpt",
  "chat.openai.com": "chatgpt",
  "perplexity.ai": "perplexity",
  "claude.ai": "claude",
  "gemini.google.com": "gemini",
  "copilot.microsoft.com": "copilot",
  "copilot.com": "copilot",
  "chat.deepseek.com": "deepseek",
  "grok.com": "grok",
  "meta.ai": "meta_ai",
  "chat.mistral.ai": "mistral",
  "you.com": "you",
  "poe.com": "poe",
  "phind.com": "phind",
};

/** utm_source values AI assistants put on their own links (ChatGPT: utm_source=chatgpt.com). */
const AI_UTM_SOURCES: Record<string, string> = {
  "chatgpt.com": "chatgpt", chatgpt: "chatgpt", "chat.openai.com": "chatgpt", openai: "chatgpt",
  "perplexity.ai": "perplexity", perplexity: "perplexity",
  "claude.ai": "claude", claude: "claude",
  "gemini.google.com": "gemini", gemini: "gemini",
  "copilot.microsoft.com": "copilot", "copilot.com": "copilot", copilot: "copilot",
  "chat.deepseek.com": "deepseek", deepseek: "deepseek",
  "grok.com": "grok", grok: "grok",
};

/** Paid click-ID params → source/medium when no explicit UTM is present. */
const CLICK_ID_SOURCES: Record<string, ClassifiedSource> = {
  gclid: { source: "google", medium: "cpc" },
  gbraid: { source: "google", medium: "cpc" }, // iOS app/web clicks carry gbraid/wbraid, not gclid
  wbraid: { source: "google", medium: "cpc" },
  gad_source: { source: "google", medium: "cpc" },
  dclid: { source: "google", medium: "display" },
  msclkid: { source: "bing", medium: "cpc" },
  fbclid: { source: "facebook", medium: "paid_social" },
  ttclid: { source: "tiktok", medium: "paid_social" },
  li_fat_id: { source: "linkedin", medium: "paid_social" },
  twclid: { source: "x", medium: "paid_social" },
};

export function referrerDomain(referrerUrl: string | null | undefined): string | null {
  if (!referrerUrl) return null;
  try {
    return new URL(referrerUrl).hostname.replace(/^www\./, "").toLowerCase() || null;
  } catch {
    return null;
  }
}

export function isInternalReferrer(referrerUrl: string | null | undefined): boolean {
  const domain = referrerDomain(referrerUrl);
  if (!domain) return false;
  return INTERNAL_HOSTS.some((h) =>
    h.startsWith(".") ? domain.endsWith(h) : domain === h || domain.endsWith(`.${h}`)
  );
}

/**
 * Classify traffic into source/medium. Priority:
 *  1. Explicit utm_source (+ utm_medium)
 *  2. Paid click IDs (gclid, fbclid, …)
 *  3. External referrer domain (search / social / AI / referral)
 *  4. Direct
 */
export function classifyTraffic(input: {
  utm_source?: string | null;
  utm_medium?: string | null;
  referrer_url?: string | null;
  click_ids?: Record<string, string> | null;
}): ClassifiedSource {
  const utmSource = input.utm_source?.trim().toLowerCase();
  if (utmSource) {
    const utmMedium = input.utm_medium?.trim().toLowerCase() || "referral";
    const ai = AI_UTM_SOURCES[utmSource];
    if (ai && ["referral", "campaign", "ai", "organic"].includes(utmMedium)) {
      return { source: ai, medium: "ai" };
    }
    return { source: utmSource, medium: input.utm_medium?.trim().toLowerCase() || "campaign" };
  }

  for (const [param, classified] of Object.entries(CLICK_ID_SOURCES)) {
    if (input.click_ids?.[param]) return classified;
  }

  const domain = referrerDomain(input.referrer_url);
  if (domain && !isInternalReferrer(input.referrer_url)) {
    // AI first: gemini.google.com must not match google/organic.
    for (const [key, name] of Object.entries(AI_ASSISTANTS)) {
      if (domain === key || domain.endsWith(`.${key}`)) {
        return { source: name, medium: "ai" };
      }
    }
    for (const [key, name] of Object.entries(SEARCH_ENGINES)) {
      if (domain === key || domain.includes(`${key}.`) || domain.startsWith(`${key}`)) {
        return { source: name, medium: "organic" };
      }
    }
    for (const [key, name] of Object.entries(SOCIAL_NETWORKS)) {
      const needle = key.replace("_com", ".com");
      if (domain === needle || domain.includes(needle)) {
        return { source: name, medium: "social" };
      }
    }
    return { source: domain, medium: "referral" };
  }

  return { source: "direct", medium: "none" };
}
